import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemDetailSectionWrapper } from '@/experiences/sales-portal/domain/items/components/item-detail-section-wrapper';
import { VariantPackCard } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/details-tab/variant-pack/variant-pack-card';
import { View } from 'react-native';
import type { RelatedVariantPack } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';

export interface VariantPackListProps {
  isQuantityDisabled?: boolean;
  onSelectVariantPack: (variantPackId: string) => void;
  saveCart: () => Promise<unknown>;
  variantPacks: RelatedVariantPack[];
}

/**
 * Related variant packs section for the item-detail Details tab. Renders one
 * {@link VariantPackCard} per sibling. The parent owns the scroll context
 * so the list is laid out as a `View` rather than a virtualized list — see
 * the inline comment on the `.map` below.
 *
 * @param props - {@link VariantPackListProps}.
 * @returns The rendered section, or `null` when there are no sibling packs.
 */
export const VariantPackList: React.FC<VariantPackListProps> = ({
  isQuantityDisabled,
  onSelectVariantPack,
  saveCart,
  variantPacks,
}) => {
  const [styles] = useStyles();

  if (variantPacks.length === 0) {
    return null;
  }

  return (
    <ItemDetailSectionWrapper
      title={i18n.t('sales_portal.order_entry.item_detail.variant_packs.title')}
    >
      {/* The Details tab owns the scroll view; rendering cards via .map keeps
       * them inside that single scroll context rather than nesting a
       * VirtualizedList inside a ScrollView. */}
      <View style={styles.cards}>
        {variantPacks.map((vp) => (
          <VariantPackCard
            isQuantityDisabled={isQuantityDisabled}
            key={vp.variantPackId}
            onSaveCart={saveCart}
            onSelect={onSelectVariantPack}
            variantPack={vp}
          />
        ))}
      </View>
    </ItemDetailSectionWrapper>
  );
};

const useStyles = createStyles((theme) => ({
  cards: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
